import { Box, Collapse, FormGroup, Icon, Stack } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { FC, useState } from 'react';
import { StyledViewMore } from './CfCheckboxList.styles';
import {
  CfCheckbox,
  CfCheckboxIcon,
  CfCheckboxSize,
  CfCheckboxVariant,
  CheckboxItem,
} from './components/Checkbox/CfCheckbox';

export type CfCheckboxListProps =
  | {
      items: CheckboxItem[];
      onCheckedChange?: (item: CheckboxItem) => void;
      length?: number;
      variant?: CfCheckboxVariant;
      icon?: CfCheckboxIcon;
      dataTestId?: string;
      dataAltTestId?: string;
      sx?: SxProps<Theme>;
      checkboxStyle?: SxProps<Theme>;
      checkboxSize?: CfCheckboxSize;
      gap?: number | string | ((theme: Theme) => string | number);
      loadingPlaceholder?: never;
    }
  | {
      loadingPlaceholder: true;
      maxCheckboxCount: number;
      sx?: SxProps<Theme>;
      gap?: number | string | ((theme: Theme) => string | number);
    };

/**
 * @param items - Array of the checkbox items in the list
 * @param onCheckedChange - Method to call on selection
 * @param length - Number of items in the list. Used to calculate overflow
 * @param variant - Styling variant to use (primary, secondary)
 * @param icon - The icon to use to signify if the item is selected
 * @param dataTestId - Test id of the checkbox
 * @param dataAltTestId - Alternative test id of the checkbox
 * @param sx - Styling of the list's wrapping box
 * @param checkboxStyle - Styling of the checkbox
 * @param checkboxSize - Size of the checkbox
 * @param gap - The gap between the checkbox items
 */
export const CfCheckboxList: FC<CfCheckboxListProps> = (props: CfCheckboxListProps) => {
  const gap = props.gap ?? 0.5;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  if (props.loadingPlaceholder) {
    return (
      <Box sx={props.sx}>
        <Stack gap={gap}>
          {Array.from(Array(Math.floor(Math.random() * props.maxCheckboxCount) + 1).keys()).map((num: number) => {
            return <CfCheckbox key={num} loadingPlaceholder />;
          })}
        </Stack>
      </Box>
    );
  }

  const handleCheckedChange = (item: CheckboxItem): void => {
    if (props.onCheckedChange) {
      props.onCheckedChange(item);
    }
  };

  const length = props.length ?? 6;
  const displayOverflowToggle = length != undefined && length < props.items.length;
  const baseLength = displayOverflowToggle && length ? length : props.items.length;

  const childrenBase: React.ReactNode[] = [];
  const childrenOverflow: React.ReactNode[] = [];
  props.items.forEach((item, index) => {
    const checkBox = (
      <CfCheckbox
        key={index}
        item={item}
        onCheckedChanged={handleCheckedChange}
        variant={props.variant ?? 'primary'}
        icon={props.icon ?? 'check'}
        hidden={displayOverflowToggle && !isExpanded && index > baseLength}
        dataTestId={props.dataTestId}
        dataAltTestId={props.dataAltTestId}
        sx={props.checkboxStyle}
        size={props.checkboxSize}
      />
    );
    if (index > baseLength) childrenOverflow.push(checkBox);
    else childrenBase.push(checkBox);
  });

  return (
    <Box sx={props.sx}>
      <FormGroup>
        <Stack gap={gap}>{childrenBase}</Stack>
      </FormGroup>
      <Collapse in={isExpanded}>
        <FormGroup sx={{ mt: 0.5 }}>
          <Stack gap={gap}>{childrenOverflow}</Stack>
        </FormGroup>
      </Collapse>
      {displayOverflowToggle && (
        <Box
          sx={{ mt: 2 }}
          tabIndex={0}
          onClick={() => setIsExpanded(!isExpanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setIsExpanded(!isExpanded);
          }}
        >
          <StyledViewMore variant='paragraphBold'>View {isExpanded ? 'Less' : 'More'}</StyledViewMore>
          <Icon
            className={isExpanded ? 'fa-caret-up' : 'fa-caret-down'}
            fontSize='small'
            sx={{ paddingLeft: 1 }}
            color='primary'
          />
        </Box>
      )}
    </Box>
  );
};
